import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Row, Col, Card, CardBody, Table, Button, Input, InputGroup } from 'reactstrap';
import { FaChevronRight, FaChevronDown, FaFolder, FaFolderOpen, FaFileAlt, FaPlus, FaSearch, FaEdit, FaTrash } from 'react-icons/fa';
import './styles.css';
import SidebarLayout from '../../../../layouts/SidebarLayout';
import sidebar from '../../utils/sidebar';
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";

const sampleAccounts = {
  id: 'root',
  name: 'Chart of Accounts',
  type: 'folder',
  children: [
    {
      id: '1',
      name: 'Assets',
      type: 'folder',
      children: [
        {
          id: '1-1',
          name: 'Current Assets',
          type: 'folder',
          children: [
            { id: '1-1-1', name: 'Cash', type: 'account', code: '1010', balance: 50000, debit: 50000, credit: 0 },
            { id: '1-1-2', name: 'Accounts Receivable', type: 'account', code: '1020', balance: 25000, debit: 25000, credit: 0 },
            { id: '1-1-3', name: 'Inventory', type: 'account', code: '1030', balance: 15000, debit: 15000, credit: 0 }
          ]
        },
        {
          id: '1-2',
          name: 'Fixed Assets',
          type: 'folder',
          children: [
            { id: '1-2-1', name: 'Equipment', type: 'account', code: '1510', balance: 100000, debit: 100000, credit: 0 },
            { id: '1-2-2', name: 'Accumulated Depreciation', type: 'account', code: '1520', balance: -20000, debit: 0, credit: 20000 }
          ]
        }
      ]
    },
    {
      id: '2',
      name: 'Liabilities',
      type: 'folder',
      children: [
        {
          id: '2-1',
          name: 'Current Liabilities',
          type: 'folder',
          children: [
            { id: '2-1-1', name: 'Accounts Payable', type: 'account', code: '2010', balance: 12000, debit: 0, credit: 12000 },
            { id: '2-1-2', name: 'Short-term Loans', type: 'account', code: '2020', balance: 8000, debit: 0, credit: 8000 }
          ]
        }
      ]
    },
    {
      id: '3',
      name: 'Equity',
      type: 'folder',
      children: [
        { id: '3-1', name: "Owner's Capital", type: 'account', code: '3010', balance: 150000, debit: 0, credit: 150000 },
        { id: '3-2', name: 'Retained Earnings', type: 'account', code: '3020', balance: 75000, debit: 0, credit: 75000 }
      ]
    },
    {
      id: '4',
      name: 'Revenue',
      type: 'folder',
      children: [
        { id: '4-1', name: 'Sales Revenue', type: 'account', code: '4010', balance: 200000, debit: 0, credit: 200000 },
        { id: '4-2', name: 'Service Revenue', type: 'account', code: '4020', balance: 50000, debit: 0, credit: 50000 }
      ]
    },
    {
      id: '5',
      name: 'Expenses',
      type: 'folder',
      children: [
        {
          id: '5-1',
          name: 'Operating Expenses',
          type: 'folder',
          children: [
            { id: '5-1-1', name: 'Salaries', type: 'account', code: '5010', balance: 80000, debit: 80000, credit: 0 },
            { id: '5-1-2', name: 'Rent', type: 'account', code: '5020', balance: 24000, debit: 24000, credit: 0 },
            { id: '5-1-3', name: 'Utilities', type: 'account', code: '5030', balance: 6000, debit: 6000, credit: 0 }
          ]
        }
      ]
    }
  ]
};

function ChartPage() {
  const { t } = useTranslation();
  const [expandedFolders, setExpandedFolders] = useState(['root', '1']);
  const [selectedFolder, setSelectedFolder] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');

  const toggleFolder = (id) => {
    setExpandedFolders(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const selectFolder = (id) => {
    setSelectedFolder(id);
    if (!expandedFolders.includes(id)) {
      setExpandedFolders(prev => [...prev, id]);
    }
  };

  const findFolder = (node, id) => {
    if (node.id === id) return node;
    if (node.children) {
      for (let child of node.children) {
        const found = findFolder(child, id);
        if (found) return found;
      }
    }
    return null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const renderTreeItem = (node, level = 0) => {
    const isExpanded = expandedFolders.includes(node.id);
    const isSelected = selectedFolder === node.id;
    const hasChildren = node.children && node.children.length > 0;

    if (node.type === 'account') return null;

    return (
      <div key={node.id}>
        <div
          className={`tree-item ${isSelected ? 'selected' : ''}`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => selectFolder(node.id)}
        >
          {hasChildren && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(node.id);
              }}
              className="chevron-icon"
            >
              {isExpanded ? (
                <FaChevronDown size={12} />
              ) : (
                <FaChevronRight size={12} />
              )}
            </span>
          )}
          {!hasChildren && <span className="chevron-placeholder"></span>}
          {isExpanded && hasChildren ? (
            <FaFolderOpen className="folder-icon" />
          ) : (
            <FaFolder className="folder-icon" />
          )}
          <span className="folder-name">{node.name}</span>
        </div>
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderTreeItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const selectedFolderData = findFolder(sampleAccounts, selectedFolder);
  const accounts = selectedFolderData?.children?.filter(item => item.type === 'account') || [];
  const subfolders = selectedFolderData?.children?.filter(item => item.type === 'folder') || [];

  const filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.code.includes(searchTerm)
  );

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('accounting.chartOfAccounts')} />
        <div className="subheader-page-content scrollable">
          <Row className="g-3">
            <Col md={3}>
              <Card className="sidebar-card">
                <CardBody className="p-0">
                  <div className="sidebar-header">
                    <h5 className="mb-0">{t('accounting.accountCategories')}</h5>
                  </div>
                  <div className="tree-container">
                    {sampleAccounts.children.map(child => renderTreeItem(child, 0))}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={9}>
              <Card className="content-card">
                <CardBody>
                  <div className="content-header">
                    <div>
                      <h4 className="folder-title">{selectedFolderData?.name}</h4>
                      <p className="folder-info">
                        {accounts.length} {t('accounting.accounts')}
                        {subfolders.length > 0 && ` • ${subfolders.length} ${t('accounting.subfolders')}`}
                      </p>
                    </div>
                    <div className="header-actions">
                      <Button color="primary" size="sm">
                        <FaPlus className="me-2" />
                        {t('accounting.addAccount')}
                      </Button>
                    </div>
                  </div>

                  {accounts.length > 0 && (
                    <div className="search-section">
                      <InputGroup>
                        <span className="input-group-text">
                          <FaSearch />
                        </span>
                        <Input
                          type="text"
                          placeholder={t('accounting.searchAccounts')}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </div>
                  )}

                  {subfolders.length > 0 && (
                    <div className="subfolders-section">
                      <h6 className="section-title">{t('accounting.subfolders')}</h6>
                      <Row>
                        {subfolders.map(folder => (
                          <Col md={4} key={folder.id} className="mb-3">
                            <div
                              className="subfolder-card"
                              onClick={() => selectFolder(folder.id)}
                            >
                              <FaFolder className="subfolder-icon" />
                              <span className="subfolder-name">{folder.name}</span>
                              <FaChevronRight className="subfolder-arrow" />
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}

                  {accounts.length > 0 ? (
                    <div className="accounts-section">
                      <h6 className="section-title">{t('accounting.accounts')}</h6>
                      <Table className="accounts-table" hover responsive>
                        <thead>
                          <tr>
                            <th>{t('accounting.code')}</th>
                            <th>{t('accounting.accountName')}</th>
                            <th className="text-end">{t('accounting.debit')}</th>
                            <th className="text-end">{t('accounting.credit')}</th>
                            <th className="text-end">{t('accounting.balance')}</th>
                            <th className="text-center">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAccounts.map(account => (
                            <tr key={account.id}>
                              <td>
                                <span className="account-code">{account.code}</span>
                              </td>
                              <td>
                                <FaFileAlt className="account-icon" />
                                {account.name}
                              </td>
                              <td className="text-end">{formatCurrency(account.debit)}</td>
                              <td className="text-end">{formatCurrency(account.credit)}</td>
                              <td className={`text-end ${account.balance < 0 ? 'text-danger' : 'text-success'}`}>
                                <strong>{formatCurrency(Math.abs(account.balance))}</strong>
                              </td>
                              <td className="text-center">
                                <Button color="link" size="sm" className="action-btn">
                                  <FaEdit />
                                </Button>
                                <Button color="link" size="sm" className="action-btn text-danger">
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    !subfolders.length && (
                      <div className="empty-state">
                        <FaFolder className="empty-icon" />
                        <h5>{t('accounting.noAccountsFound')}</h5>
                        <p>{t('accounting.folderEmpty')}</p>
                        <Button color="primary" size="sm">
                          <FaPlus className="me-2" />
                          {t('accounting.addFirstAccount')}
                        </Button>
                      </div>
                    )
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </SidebarLayout>
  );
}

export default ChartPage;
